import { useEffect } from "react";

const THRESHOLD = 80;
const MAX_PULL = 120;
const SVG_NS = "http://www.w3.org/2000/svg";

/** SVG リフレッシュアイコンを DOM API で生成 */
const createRefreshSvg = (): SVGSVGElement => {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("width", "20");
  svg.setAttribute("height", "20");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.style.color = "hsl(0 0% 25%)";
  svg.style.transition = "transform 0.15s ease";

  const polyline1 = document.createElementNS(SVG_NS, "polyline");
  polyline1.setAttribute("points", "17 1 21 5 17 9");
  const path1 = document.createElementNS(SVG_NS, "path");
  path1.setAttribute("d", "M3 11V9a4 4 0 0 1 4-4h14");
  const polyline2 = document.createElementNS(SVG_NS, "polyline");
  polyline2.setAttribute("points", "7 23 3 19 7 15");
  const path2 = document.createElementNS(SVG_NS, "path");
  path2.setAttribute("d", "M21 13v2a4 4 0 0 1-4 4H3");

  svg.append(polyline1, path1, polyline2, path2);
  return svg;
};

/** インジケーター要素を生成して DOM に挿入 */
const createIndicator = (): {
  container: HTMLDivElement;
  svg: SVGSVGElement;
} => {
  const el = document.createElement("div");
  el.id = "ptr-indicator";
  el.style.cssText = [
    "position: fixed",
    "top: 0",
    "left: 50%",
    "transform: translate(-50%, -48px)",
    "width: 36px",
    "height: 36px",
    "border-radius: 50%",
    "background: hsl(0 0% 98%)",
    "box-shadow: 0 2px 8px rgba(0,0,0,0.15)",
    "display: flex",
    "align-items: center",
    "justify-content: center",
    "z-index: 9999",
    "transition: transform 0.2s ease",
    "pointer-events: none",
  ].join(";");

  const svg = createRefreshSvg();
  el.appendChild(svg);
  document.body.appendChild(el);
  return { container: el, svg };
};

/** スピナー回転アニメーション CSS を注入 */
const injectSpinnerStyle = () => {
  if (document.getElementById("ptr-spinner-style")) return;
  const style = document.createElement("style");
  style.id = "ptr-spinner-style";
  style.textContent = `
    @keyframes ptr-spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
};

/** iOS PWA standalone モードで pull-to-refresh を有効にするフック */
export const usePullToRefresh = () => {
  useEffect(() => {
    const isIosStandalone =
      (navigator as unknown as { standalone?: boolean }).standalone === true;
    if (!isIosStandalone) return;

    injectSpinnerStyle();
    const { container: indicator, svg } = createIndicator();

    let startY = 0;
    let pulling = false;
    let reachedThreshold = false;

    const updateIndicator = (distance: number) => {
      const progress = Math.min(distance / THRESHOLD, 1);
      const translateY = -48 + progress * 68;
      indicator.style.transform = `translate(-50%, ${translateY}px)`;
      const rotation = progress * 360;
      svg.style.transform = `rotate(${rotation}deg)`;
    };

    const hideIndicator = () => {
      indicator.style.transition = "transform 0.3s ease";
      indicator.style.transform = "translate(-50%, -48px)";
      svg.style.animation = "";
      setTimeout(() => {
        indicator.style.transition = "transform 0.2s ease";
      }, 300);
    };

    const startSpinAndReload = () => {
      svg.style.transform = "";
      svg.style.animation = "ptr-spin 0.6s linear infinite";
      indicator.style.transform = "translate(-50%, 20px)";
      setTimeout(() => {
        window.location.reload();
      }, 400);
    };

    /** Drawer・モーダル内、またはドラッグ中のタッチ操作を無視する */
    const shouldIgnore = (target: EventTarget | null): boolean => {
      if (document.body.dataset.dragging) return true;
      if (!(target instanceof HTMLElement)) return false;
      return (
        target.closest("[data-slot='drawer-content'], [role='dialog']") !== null
      );
    };

    /** タッチ対象の最も近いスクロールコンテナが最上部にいるか判定する */
    const isAtScrollTop = (target: EventTarget | null): boolean => {
      if (window.scrollY > 1) return false;
      if (!(target instanceof HTMLElement)) return true;
      let el: HTMLElement | null = target;
      while (el && el !== document.body) {
        const { overflowY } = getComputedStyle(el);
        if (overflowY === "auto" || overflowY === "scroll") {
          return el.scrollTop <= 1;
        }
        el = el.parentElement;
      }
      return true;
    };

    const onTouchStart = (e: TouchEvent) => {
      if (isAtScrollTop(e.target) && !shouldIgnore(e.target)) {
        startY = e.touches[0].pageY;
        pulling = true;
        reachedThreshold = false;
        indicator.style.transition = "transform 0.2s ease";
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!pulling) return;

      const currentY = e.touches[0].pageY;
      const distance = currentY - startY;

      if (distance <= 0) {
        pulling = false;
        hideIndicator();
        document.body.style.transform = "";
        return;
      }

      const pullDistance = Math.min(distance * 0.4, MAX_PULL);
      document.body.style.transform = `translateY(${pullDistance}px)`;
      document.body.style.transition = "none";

      updateIndicator(distance);
      reachedThreshold = distance > THRESHOLD;
    };

    const onTouchEnd = () => {
      if (!pulling) return;

      if (reachedThreshold) {
        document.body.style.transition = "transform 0.3s ease";
        document.body.style.transform = "translateY(48px)";
        startSpinAndReload();
      } else {
        document.body.style.transition = "transform 0.3s ease";
        document.body.style.transform = "";
        hideIndicator();
        setTimeout(() => {
          document.body.style.transition = "";
        }, 300);
      }

      pulling = false;
      startY = 0;
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", onTouchEnd);

    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      indicator.remove();
      document.getElementById("ptr-spinner-style")?.remove();
      document.body.style.transform = "";
      document.body.style.transition = "";
    };
  }, []);
};
